import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import axios from '../../../utils/axios';

const OwnerManagement = ({ asset, isEditing, onUpdate }) => {
  const [showChangeOwner, setShowChangeOwner] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newOwner, setNewOwner] = useState({
    name: '',
    email: '',
    department: '',
    employeeId: '',
    userId: '',
    notifyOwner: true
  });

  // Fetch users from backend (try multiple endpoints to guarantee loading)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        let response;
        try {
          response = await axios.get('/admin/users');
        } catch (e1) {
          try {
            response = await axios.get('/users');
          } catch (e2) {
            response = await axios.get('/user-management');
          }
        }
        
        let usersData = [];
        if (Array.isArray(response?.data)) {
          usersData = response.data;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          usersData = response.data.data;
        } else if (response?.data?.users && Array.isArray(response.data.users)) {
          usersData = response.data.users;
        } else if (response?.data) {
          for (const key in response.data) {
            if (Array.isArray(response.data[key])) {
              usersData = response.data[key];
              break;
            }
          }
        }
        
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  // Initialize newOwner when asset or users list loads
  useEffect(() => {
    if (asset?.owner_id && users.length > 0) {
      const current = users.find(u => String(u.id) === String(asset.owner_id));
      if (current && !newOwner.userId) {
        setNewOwner({
          name: current.name || '',
          email: current.email || '',
          department: asset.department || current.department || '',
          employeeId: current.employee_id || '',
          userId: current.id.toString(),
          notifyOwner: true
        });
      }
    } else if (asset?.owner && !newOwner.userId) {
      setNewOwner({
        name: asset.owner.name || '',
        email: asset.owner.email || '',
        department: asset.department || asset.owner.department || '',
        employeeId: asset.owner.employee_id || '',
        userId: asset.owner.id ? asset.owner.id.toString() : '',
        notifyOwner: true
      });
    }
  }, [asset?.owner_id, asset?.owner, users]);

  // Map users to employee options format
  const employeeOptions = users.map(user => ({
    value: user.id.toString(),
    label: `${user.name}${user.department ? ` - ${user.department}` : ''}${user.employee_id ? ` (${user.employee_id})` : ''}`,
    department: user.department || '',
    employeeId: user.employee_id || '',
    email: user.email || '',
    name: user.name || ''
  }));

  // Get ownership history from asset - prioritizing actual ownership changes
  const getOwnershipHistory = () => {
    if (asset?.histories && Array.isArray(asset.histories) && asset.histories.length > 0) {
      const ownerHistories = asset.histories.filter(h => 
        h.action === 'Owner Reassigned' || 
        h.action === 'Asset Created' || 
        h.action === 'Asset Assigned' ||
        (h.details && (h.details.toLowerCase().includes('owner') || h.details.toLowerCase().includes('assigned')))
      );
      
      if (ownerHistories.length > 0) {
        return ownerHistories.map(h => ({
          id: h.id,
          owner: h.user || asset?.owner || { name: 'Assigned User' },
          user: h.user || asset?.owner,
          assignedDate: h.created_at,
          isCurrent: h.user_id ? (String(h.user_id) === String(asset?.owner_id)) : true,
          details: h.details
        }));
      }
    }
    
    if (asset?.ownershipHistory && asset.ownershipHistory.length > 0) {
      return asset.ownershipHistory;
    }
    
    if (asset?.owner) {
      return [{
        id: 'current',
        owner: asset.owner,
        isCurrent: true,
        assignedDate: asset.created_at || new Date().toISOString(),
        user: asset.owner
      }];
    }
    
    return [];
  };

  const ownershipHistory = getOwnershipHistory();
  const currentOwner = asset?.owner || null;

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Handle employee selection: Immediately updates local state & parent asset state
  const handleOwnerChange = (userId) => {
    const selectedUser = users.find(u => String(u.id) === String(userId));
    if (selectedUser) {
      const updated = {
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        department: selectedUser.department || asset?.department || '',
        employeeId: selectedUser.employee_id || '',
        userId: selectedUser.id.toString(),
        notifyOwner: true
      };
      setNewOwner(updated);

      // Immediately propagate to parent asset so header and save button reflect this
      if (typeof onUpdate === 'function') {
        onUpdate({
          ...asset,
          owner_id: selectedUser.id,
          owner: selectedUser,
          department: selectedUser.department || asset?.department
        });
      }
    } else {
      setNewOwner({
        name: '',
        email: '',
        department: '',
        employeeId: '',
        userId: '',
        notifyOwner: true
      });
    }
  };

  // Handle assigning new owner
  const handleAssignOwner = async () => {
    if (!newOwner.userId) {
      toast.error('Please select a user');
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        ...asset,
        owner_id: parseInt(newOwner.userId),
        department: newOwner.department || asset?.department,
        notify_owner: newOwner.notifyOwner
      };

      const response = await axios.put(`/assets/${asset.id}`, updateData);
      
      if (response.data && response.data.success !== false) {
        const updatedAsset = response.data.data || response.data;
        
        if (typeof onUpdate === 'function') {
          onUpdate(updatedAsset, false);
        }

        // Send confirmation in-app notification & email
        if (newOwner.notifyOwner && (newOwner.email || newOwner.userId)) {
          try {
            await axios.post('/notifications/send', {
              user_id: parseInt(newOwner.userId),
              to: newOwner.email,
              name: newOwner.name,
              subject: `Asset Assignment Notification: ${asset.asset_id || asset.name}`,
              title: `Asset Assigned: ${asset.asset_id || asset.name}`,
              category: 'asset_assignment',
              message: `You have been assigned as the custodian for asset ${asset.asset_id || asset.name} (${asset.brand || ''} ${asset.model || ''}) in department ${newOwner.department || asset.department}.`,
              action_url: `/asset-detail-management?id=${asset.id}`
            });
          } catch (notifyError) {
            console.error('Frontend notification dispatch notice:', notifyError);
          }
        }

        toast.success(`Owner assigned to ${newOwner.name}. Notification dispatched.`);
      } else {
        throw new Error(response.data?.message || 'Failed to update asset');
      }
    } catch (error) {
      console.error('Error assigning owner:', error);
      toast.error(error.response?.data?.message || 'Failed to assign owner. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get owner display name
  const getOwnerName = (owner) => {
    if (!owner) return 'Unassigned';
    return owner.name || owner.ownerName || owner.owner?.name || 'Unknown';
  };

  // Get owner email
  const getOwnerEmail = (owner) => {
    if (!owner) return 'No email';
    return owner.email || owner.ownerEmail || owner.owner?.email || 'No email';
  };

  // Get owner department
  const getOwnerDepartment = (owner) => {
    if (!owner) return 'No department';
    return owner.department || owner.owner?.department || 'No department';
  };

  // Get owner employee ID
  const getOwnerEmployeeId = (owner) => {
    if (!owner) return 'No ID';
    return owner.employee_id || owner.employeeId || owner.owner?.employee_id || 'No ID';
  };

  // Get assigned date
  const getAssignedDate = (historyItem) => {
    if (!historyItem) return null;
    return historyItem.assignedDate || 
           historyItem.created_at || 
           historyItem.updated_at || 
           historyItem.timestamp || 
           null;
  };

  // Check if current owner
  const isCurrentOwner = (historyItem) => {
    if (!historyItem) return false;
    if (historyItem.isCurrent) return true;
    if (historyItem.owner && currentOwner) {
      return historyItem.owner.id === currentOwner.id || 
             historyItem.owner_id === currentOwner.id;
    }
    return false;
  };

  // Get owner from history item
  const getOwnerFromHistory = (historyItem) => {
    if (historyItem.owner) return historyItem.owner;
    if (historyItem.user) return historyItem.user;
    if (historyItem.owner_id) {
      const foundUser = users.find(u => String(u.id) === String(historyItem.owner_id));
      return foundUser || historyItem;
    }
    return historyItem;
  };

  // Check if history item is valid
  const isValidHistoryItem = (item) => {
    if (!item) return false;
    const owner = getOwnerFromHistory(item);
    return owner && (owner.name || owner.ownerName || owner.owner?.name);
  };

  const validHistory = ownershipHistory.filter(isValidHistoryItem);

  const isPendingChange = newOwner.userId && String(newOwner.userId) !== String(asset?.owner_id);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Owner Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage asset custody, assignment, and transfer notifications
          </p>
        </div>
        {isEditing && (
          <Button
            variant={showChangeOwner ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowChangeOwner(!showChangeOwner)}
            iconName="UserCheck"
            iconPosition="left"
          >
            {showChangeOwner ? 'Hide Assignment Form' : 'Reassign Owner'}
          </Button>
        )}
      </div>

      {/* Current / Selected Owner Information Card */}
      <div className={`rounded-lg p-5 mb-6 border transition-all ${
        isPendingChange 
          ? 'bg-blue-50/60 border-blue-400 dark:bg-blue-950/20' 
          : 'bg-muted/70 border-border'
      }`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white shadow-sm ${
              isPendingChange ? 'bg-blue-600' : 'bg-primary'
            }`}>
              <Icon name="User" size={24} color="white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-xl font-bold text-foreground">
                  {newOwner.userId && newOwner.name ? newOwner.name : getOwnerName(currentOwner)}
                </h4>
                {isPendingChange ? (
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-600 text-white animate-pulse">
                    Selected for Assignment
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Active Custodian
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {newOwner.userId && newOwner.email ? newOwner.email : getOwnerEmail(currentOwner)}
              </p>
              <div className="flex items-center flex-wrap gap-4 mt-3 text-sm">
                <span className="flex items-center space-x-1.5 bg-background/80 px-2.5 py-1 rounded border border-border">
                  <Icon name="Building" size={14} className="text-muted-foreground" />
                  <span className="font-medium text-foreground">
                    {newOwner.userId && newOwner.department ? newOwner.department : getOwnerDepartment(currentOwner)}
                  </span>
                </span>
                <span className="flex items-center space-x-1.5 bg-background/80 px-2.5 py-1 rounded border border-border">
                  <Icon name="Badge" size={14} className="text-muted-foreground" />
                  <span className="font-medium text-foreground">
                    ID: {newOwner.userId && newOwner.employeeId ? newOwner.employeeId : getOwnerEmployeeId(currentOwner)}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="text-muted-foreground">Assigned Date</p>
            <p className="font-semibold text-foreground">
              {formatDate(asset?.created_at || asset?.updated_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Change Owner Form - Automatically visible in Edit Mode */}
      {isEditing && showChangeOwner && (
        <div className="bg-accent/5 border border-primary/30 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-foreground flex items-center gap-2">
              <Icon name="UserPlus" size={18} className="text-primary" />
              Select & Assign New Custodian
            </h4>
            <span className="text-xs text-muted-foreground">
              Instant preview & automated notification
            </span>
          </div>
          
          <div className="space-y-4">
            {/* User Selection Dropdown */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex justify-between items-center">
                <span>Select Employee / User *</span>
                {users.length > 0 && (
                  <span className="text-xs text-green-600 font-medium">
                    ✓ {users.length} available employees loaded
                  </span>
                )}
              </label>
              
              <select
                className="w-full px-3.5 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-medium"
                value={newOwner.userId}
                onChange={(e) => handleOwnerChange(e.target.value)}
                disabled={loadingUsers}
              >
                <option value="">
                  {loadingUsers ? 'Loading employees from database...' : '-- Select an employee to assign --'}
                </option>
                {employeeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {loadingUsers && (
                <p className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                  <span className="inline-block animate-spin">⟳</span>
                  Loading users...
                </p>
              )}
              
              {!loadingUsers && employeeOptions.length === 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800">
                  No employees found. Please add users in User Management first.
                </div>
              )}
            </div>

            {/* Read-only / Confirmation fields populated from user */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Employee Name
                </label>
                <input
                  type="text"
                  value={newOwner.name}
                  disabled
                  readOnly
                  placeholder="Selected employee name"
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-muted text-foreground"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Notification Email
                </label>
                <input
                  type="text"
                  value={newOwner.email}
                  disabled
                  readOnly
                  placeholder="Notification will be sent here"
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-muted text-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Employee ID
                </label>
                <input
                  type="text"
                  value={newOwner.employeeId}
                  disabled
                  readOnly
                  placeholder="Employee ID"
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-muted text-foreground"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Department
                </label>
                <input
                  type="text"
                  value={newOwner.department}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewOwner(prev => ({ ...prev, department: val }));
                    if (typeof onUpdate === 'function') {
                      onUpdate({ ...asset, department: val });
                    }
                  }}
                  disabled={!newOwner.userId}
                  placeholder="Department (auto-filled or editable)"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground"
                />
              </div>
            </div>

            {/* Notification Checkbox */}
            <div className="flex items-center space-x-2.5 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
              <input
                type="checkbox"
                id="notifyOwnerCheckbox"
                checked={newOwner.notifyOwner}
                onChange={(e) => setNewOwner({ ...newOwner, notifyOwner: e.target.checked })}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <label htmlFor="notifyOwnerCheckbox" className="text-sm font-medium text-foreground cursor-pointer flex-1">
                Send in-app notification and email confirmation to {newOwner.name ? <strong>{newOwner.name}</strong> : 'new owner'}
                {newOwner.email && <span className="text-xs text-muted-foreground block">Recipient: {newOwner.email}</span>}
              </label>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button 
                variant="default" 
                onClick={handleAssignOwner}
                disabled={!newOwner.userId || isSubmitting}
                iconName={isSubmitting ? 'Loader2' : 'CheckCircle2'}
                iconPosition="left"
              >
                {isSubmitting ? 'Assigning...' : 'Confirm & Assign Owner'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ownership History */}
      {validHistory && validHistory.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-foreground mb-4">Ownership History</h4>
          <div className="space-y-3">
            {validHistory.map((historyItem, index) => {
              const owner = getOwnerFromHistory(historyItem);
              const isCurrent = isCurrentOwner(historyItem);
              
              return (
                <div 
                  key={historyItem.id || index} 
                  className={`flex items-center justify-between p-3.5 border rounded-lg ${
                    isCurrent ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      isCurrent ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name="User" size={16} />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {getOwnerName(owner)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getOwnerDepartment(owner)} • {getOwnerEmail(owner)}
                      </p>
                      {historyItem.details && (
                        <p className="text-xs text-muted-foreground mt-0.5 italic">
                          {historyItem.details}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right text-xs">
                    <div className="flex items-center space-x-2 mb-1 justify-end">
                      <span className="text-muted-foreground">Assigned:</span>
                      <span className="text-foreground font-medium">
                        {formatDate(getAssignedDate(historyItem))}
                      </span>
                    </div>
                    {isCurrent && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary text-white mt-1">
                        Current Owner
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show message if no history */}
      {(!validHistory || validHistory.length === 0) && currentOwner && (
        <div className="text-center py-4 text-muted-foreground">
          <p>No ownership history available</p>
        </div>
      )}

      {/* Quick Actions */}
      {currentOwner && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              iconName="Mail" 
              iconPosition="left"
              onClick={() => {
                const email = getOwnerEmail(currentOwner);
                if (email && email !== 'No email') {
                  window.location.href = `mailto:${email}`;
                } else {
                  toast.error('No email address available');
                }
              }}
            >
              Contact Owner
            </Button>
            <Button variant="outline" size="sm" iconName="Calendar" iconPosition="left">
              Schedule Handover
            </Button>
            <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
              Generate Report
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerManagement;